import React, { useState, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Card,
    styled,
    Button,
    IconButton,
    OutlinedInput,
    Chip
} from '@mui/material';

import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from "react-router-dom"
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from "../common/CommonSectionHeader";
import CommonRestricted from '../common/CommonRestricted';
import { CommonIcons, icons } from "../common/CommonIcons";
import { spliceString } from "../../utils/formatString";

import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';
import { useAuditTrails, auditTrailTypes, auditTargetTypes, moduleTypes, targetTypes } from '../../hooks/api/useAuditTrails';

import { useSystemPrompt } from "../../hooks/useSystemPrompt";
import { initialValues, validationSchema, initialRowValues } from "../../schema/audit/auditTrailSchema"
import { checkValueExists } from "../../utils/dataSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewAuditTrail({
    routeType
}) {

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useAuditTrails({ auditTrailType: auditTrailTypes.auditTrail, targetType: targetTypes.auditTrail });

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.audit.id, scopeTypes.audit.auditTrail.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const [dataModule, setDataModule] = useState()

    const onSubmit = async (currentValues) => {

        let tags = []

        currentValues.rows.map(item => {
            tags.push({
                tag_key: item?.tagKey,
                tag_label: item?.tagLabel,
                tag_value: item?.tagValue,
                tag_description: item?.tagDescription,
            })
            return false;
        })

        try {

            let data = {
                audit_trail: {
                    target: currentValues?.auditTarget,
                    module: currentValues?.auditModule,
                    trail_ref: currentValues?.trailRef,
                    type: currentValues?.type,
                    description: currentValues?.description,
                    refer_id: currentValues?.referId,
                },
                tags
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/auditTrails"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const handleAddItem = ({ rows, setFieldValue }) => {

        rows.push(initialRowValues);
        setFieldValue('rows', rows)

    }

    const handleRemoveItem = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, `Items at least have (1) row`)
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const RenderAuditInfo = () => {

        if (!values) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={'Trail Info'}
                        subTitle={'Details of audit trail'}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.auditTarget && Boolean(errors.auditTarget)}
                            >
                                <InputLabel id="auditTarget">Target</InputLabel>
                                <Select
                                    labelId="auditTarget"
                                    label="Target"
                                    // name={"auditTarget"}
                                    onChange={(event) => {
                                        setFieldValue("auditTarget", event.target.value)
                                    }}
                                    value={values.auditTarget}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        Object.keys(auditTargetTypes).map((i, index) => {

                                            let item = auditTargetTypes[i]

                                            return (
                                                <MenuItem key={index} value={item.value}>{item.display}</MenuItem>
                                            )

                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.auditTarget && errors.auditTarget}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.moduleScope && Boolean(errors.moduleScope)}
                            >
                                <InputLabel id="moduleScope">Scope</InputLabel>
                                <Select
                                    labelId="moduleScope"
                                    label="Module"
                                    // name={"moduleScope"}
                                    onChange={(event) => {
                                        setFieldValue("moduleScope", event.target.value)
                                    }
                                    }
                                    value={values.moduleScope}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        Object.keys(moduleTypes).map((i, index) => {

                                            return (
                                                <MenuItem key={index} value={i}>{i}</MenuItem>
                                            )

                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.moduleScope && errors.moduleScope}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                            // error={touched.auditModule && Boolean(errors.auditModule)}
                            >
                                <InputLabel id="auditModule">Module</InputLabel>
                                <Select
                                    labelId="auditModule"
                                    label="Module"
                                    name={"auditModule"}
                                    onChange={(event) => {

                                        let auditModule = event.target.value

                                        setFieldValue("auditModule", auditModule)

                                    }}
                                    value={values?.auditModule}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        values?.moduleScope &&

                                        Object.keys(moduleTypes[values?.moduleScope]).map((i, index) => {

                                            let item = moduleTypes[values?.moduleScope][i]

                                            return (
                                                <MenuItem key={index} value={item?.value}>{item?.display}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                {/* <FormHelperText>
                                    {touched.auditModule && errors.auditModule}
                                </FormHelperText> */}
                            </FormControl>
                        </Grid>


                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                            // error={touched.auditModule && Boolean(errors.auditModule)}
                            >
                                <InputLabel id="trailRef">Trail Ref</InputLabel>
                                <Select
                                    labelId="trailRef"
                                    label="Module"
                                    name={"trailRef"}
                                    onChange={(event) => {

                                        let trailRef = event.target.value

                                        setFieldValue("trailRef", trailRef)

                                    }}
                                    value={values.trailRef}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        values?.moduleScope &&

                                        Object.keys(moduleTypes[values?.moduleScope]).map((i, index) => {

                                            let item = moduleTypes[values?.moduleScope][i]

                                            return (
                                                <MenuItem key={index} value={item?.trailRef}>{item?.trailRef}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                {/* <FormHelperText>
                                    {touched.auditModule && errors.auditModule}
                                </FormHelperText> */}
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"type"}
                                label="Type"
                                type="text"
                                variant="outlined"
                                value={values.type}
                                onChange={handleChange}
                                error={touched.type && Boolean(errors.type)}
                                helperText={touched.type && errors.type}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"referId"}
                                label="Refer ID"
                                type="text"
                                variant="outlined"
                                value={values.referId}
                                onChange={handleChange}
                                error={touched.referId && Boolean(errors.referId)}
                                helperText={touched.referId && errors.referId}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"description"}
                                label="Description"
                                type="text"
                                multiline
                                rows={2}
                                variant="outlined"
                                value={values.description}
                                onChange={handleChange}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                            />
                        </Grid>

                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderRow = () => {

        const rows = values?.rows;

        return (
            <Box>
                <Box>
                    {
                        rows && rows.length > 0
                        && rows.map((item, index) => {

                            const itemValues = item;
                            let itemTouched = touched?.rows
                            let itemErrors = errors?.rows
                            // const itemTouched = touched?.rows

                            if (itemTouched) {
                                itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                            }

                            if (itemErrors) {
                                itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                            }

                            return (
                                <Grid key={index} item xs={12} sm={12} md={12}>

                                    <Grid container spacing={1} >

                                        <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                            <IconButton
                                                sx={{
                                                    mb: 1,
                                                    alignContent: "center"
                                                }}
                                                aria-label="delete"
                                                onClick={() => handleRemoveItem({ rows: values.rows, index, setFieldValue })}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={2.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.tagKey`}
                                                label="Tag Key"
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.tagKey ? itemValues.tagKey : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.tagKey)}
                                                helperText={itemErrors?.tagKey}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={2.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.tagLabel`}
                                                label="Tag Label"
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.tagLabel ? itemValues.tagLabel : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.tagLabel)}
                                                helperText={itemErrors?.tagLabel}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={2.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.tagValue`}
                                                label="Tag Value"
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.tagValue ? itemValues.tagValue : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.tagValue)}
                                                helperText={itemErrors?.tagValue}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={4}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.tagDescription`}
                                                label="Description"
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.tagDescription ? itemValues.tagDescription : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.tagDescription)}
                                                helperText={itemErrors?.tagDescription}
                                            />
                                        </Grid>

                                    </Grid>
                                </Grid>
                            )
                        })
                    }
                </Box>

            </Box>
        )
    }

    const RenderTagItemInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <CommonSectionHeader
                        title={'Tag Items Info'}
                        subTitle={'Details of tag information'}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    {
                        RenderRow()
                    }
                </Grid>

                <Grid item xs={12} sm={12} md={4} textAlign={"right"}>
                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        fullWidth
                        startIcon={<CommonIcons name={icons.add} />}
                        onClick={() => handleAddItem({ rows: values.rows, setFieldValue })}
                    >
                        Add Tag
                    </Button>
                </Grid>

            </>
        )
    }

    const RenderForm = () => {

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        {
                            RenderAuditInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderTagItemInfo()
                        }

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            Save Changes
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newAudit })
            }

            <CommonBreadcumbs
                pageTitle="New Audit Trail"
                breadcrumbs={[
                    {
                        displayText: "Audit Trails",
                        props: {
                            // underline: "hover",
                            href: getRoute("settings/auditTrails")
                        }
                    },
                    {
                        displayText: "New Audit Trail",
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {JSON.stringify(values)}

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}